import React, { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { checkAuth } from '../../store/slices/authSlice'
import { Result, Button, Spin } from 'antd'

interface AdminRouteProps {
  children: React.ReactNode
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { isAuthenticated, user, loading } = useAppSelector(state => state.auth)

  // 组件挂载时检查认证状态
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      console.log('AdminRoute - 检查认证状态')
      dispatch(checkAuth())
    }
  }, [dispatch, isAuthenticated, loading])

  // 调试信息
  console.log('=== AdminRoute Debug Info ===')
  console.log('Current path:', location.pathname)
  console.log('isAuthenticated:', isAuthenticated)
  console.log('loading:', loading)
  console.log('user:', user)
  console.log('user.accountType:', user?.accountType)
  console.log('localStorage token:', localStorage.getItem('token') ? 'exists' : 'missing')
  console.log('localStorage user:', localStorage.getItem('user') ? 'exists' : 'missing')

  // 仅在尚未认证时显示加载状态，避免 checkAuth 刷新时卡住
  if (loading && !isAuthenticated) {
    console.log('AdminRoute - 正在加载认证状态')
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <div className="text-white mt-4">验证管理员权限中...</div>
        </div>
      </div>
    )
  }

  // 如果未登录，重定向到管理员登录页
  if (!isAuthenticated) {
    console.log('AdminRoute - 用户未认证，重定向到管理员登录页')
    return <Navigate to="/admin/login" state={{ from: location }} replace />
  }

  // 如果不是管理员，显示无权限页面或自动设置为管理员（开发环境）
  if (user?.accountType !== 'admin') {
    console.log('AdminRoute - 用户不是管理员，当前类型:', user?.accountType)
    
    // 开发环境下的临时解决方案：自动提升权限
    if (process.env.NODE_ENV === 'development' && user) {
      console.log('AdminRoute - 开发环境，自动提升用户权限为管理员')
      const updatedUser = { ...user, accountType: 'admin' as const }
      localStorage.setItem('user', JSON.stringify(updatedUser))
      // 刷新页面以重新加载用户状态
      window.location.reload()
      return null
    }
    
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Result
          status="403"
          title="403"
          subTitle={`抱歉，您没有权限访问此页面。当前账户类型: ${user?.accountType || '未知'}`}
          extra={
            <div className="space-y-4">
              <div className="space-x-2">
                <Button type="primary" onClick={() => window.history.back()}>
                  返回
                </Button>
                <Button 
                  onClick={() => window.location.href = '/admin/login'}
                >
                  管理员登录
                </Button>
              </div>
              <div className="text-gray-400 text-sm bg-gray-800 p-4 rounded">
                <p className="font-bold mb-2">调试信息:</p>
                <p>用户邮箱: {user?.email}</p>
                <p>账户类型: {user?.accountType}</p>
                <p>需要权限: admin</p>
                <p>当前路径: {location.pathname}</p>
                <Button 
                  type="link" 
                  size="small"
                  onClick={() => {
                    // 临时提升权限（仅开发环境）
                    if (user) {
                      const updatedUser = { ...user, accountType: 'admin' as const }
                      localStorage.setItem('user', JSON.stringify(updatedUser))
                      window.location.reload()
                    }
                  }}
                  className="text-yellow-400 mt-2"
                >
                  [开发] 临时提升为管理员权限
                </Button>
              </div>
            </div>
          }
          className="bg-gray-900 text-white"
        />
      </div>
    )
  }

  console.log('AdminRoute - 权限验证通过，渲染子组件')
  return <>{children}</>
}

export default AdminRoute